import { useQuery } from "@tanstack/react-query";
import { BookOpen, ClipboardCheck, Coins, Database, Flag, Rocket } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { Alert, Badge, PageHead, Spinner } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { LIBRARY_ROOT, formatDate, libraryPath, resolveLibrary, type StackData, type TestsData } from "./library";
import { LibraryContent } from "./LibraryContent";
import { StackView } from "./StackView";
import { SourcesView, type SourcesData } from "./SourcesView";
import { TestCasesView } from "./TestCasesView";
import { eclipseMounts, type EclipseData } from "./EclipseViews";
import "./library.css";

const GROUP_ICONS: Record<string, ReactNode> = {
  stack: <Coins />,
  releases: <Rocket />,
  strategies: <Flag />,
  "data-sources": <Database />,
  quality: <ClipboardCheck />,
};

function useLibraryIndex() {
  return useQuery({ queryKey: ["library"], queryFn: () => unwrap(api.GET("/v1/admin/library")), staleTime: 5 * 60_000 });
}

function statusTone(status: string): "ok" | "warn" | "info" | undefined {
  const s = status.toLowerCase();
  if (s.startsWith("deprecated")) return undefined;
  if (s.startsWith("shipped")) return "ok";
  if (s.startsWith("planned") || s.startsWith("estimate")) return "warn";
  return "info";
}

/** "Updated Oct 7, 2026" in the reader's locale (`updated_on` is a date, YYYY-MM-DD). */
function useUpdated(): (day: string) => string {
  const intl = useIntlShape();
  return (day) => {
    const d = new Date(`${day}T12:00:00Z`);
    return Number.isNaN(d.getTime()) ? formatDate(day) : intl.formatDate(d, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
  };
}

function Tile({ index, item }: { index: S["LibraryIndex"]; item: S["LibraryItem"] }) {
  const t = useT();
  const updated = useUpdated();
  return (
    <Link className={item.status.toLowerCase().startsWith("deprecated") ? "lib-tile is-deprecated" : "lib-tile"} to={libraryPath(index, item)}>
      <div className="lib-icon">{GROUP_ICONS[item.group] ?? <BookOpen />}</div>
      <h3>{item.title}</h3>
      <p>{item.summary}</p>
      <div className="lib-tile-foot">
        <Badge tone={statusTone(item.status)}>{item.status}</Badge>
        <span>{t("library.updated", { date: updated(item.updated_on) })}</span>
      </div>
    </Link>
  );
}

/** /admin/library: every group and its pages. */
export function LibraryIndexPage() {
  const index = useLibraryIndex();
  const t = useT();
  useEffect(() => {
    document.title = t("library.index.documentTitle");
  }, [t]);
  if (index.isLoading) return <Spinner />;
  if (index.error || !index.data) return <Alert tone="danger">{errorMessage(index.error)}</Alert>;
  const data = index.data;
  return (
    <div className="lib-doc">
      <PageHead title={t("library.index.title")} description={t("library.index.description")} />
      <div className="lib-groups">
        {data.groups.map((g) => (
          <section key={g.slug} className="lib-group">
            <h2>{g.title}</h2>
            <p>{g.description}</p>
            <div className="lib-tiles">
              {data.items.filter((i) => i.group === g.slug).map((item) => (
                <Tile key={item.slug} index={data} item={item} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/** /admin/library/:group and /admin/library/:group/:slug. */
export function LibraryGroupPage() {
  const { group = "", slug } = useParams();
  const index = useLibraryIndex();
  const t = useT();
  if (index.isLoading) return <Spinner />;
  if (index.error || !index.data) return <Alert tone="danger">{errorMessage(index.error)}</Alert>;
  const found = resolveLibrary(index.data, group, slug);
  if (found.kind === "missing")
    return (
      <Alert tone="warn" title={t("library.missing.title")}>
        <Link to={LIBRARY_ROOT}>{t("library.missing.back")}</Link>
      </Alert>
    );
  if (found.kind === "group")
    return (
      <div className="lib-doc">
        <Crumbs />
        <PageHead title={found.group.title} description={found.group.description} />
        <div className="lib-tiles">
          {found.items.map((item) => (
            <Tile key={item.slug} index={index.data} item={item} />
          ))}
        </div>
      </div>
    );
  const siblings = index.data.items.filter((i) => i.group === found.group.slug);
  return <LibraryPage slug={found.item.slug} crumbs={<Crumbs group={siblings.length > 1 ? found.group : undefined} />} />;
}

function Crumbs({ group }: { group?: { slug: string; title: string } }) {
  const t = useT();
  return (
    <nav className="lib-crumbs" aria-label={t("library.crumbs.label")}>
      <Link to={LIBRARY_ROOT}>{t("library.index.title")}</Link>
      {group && (
        <>
          <span>/</span>
          <Link to={`${LIBRARY_ROOT}/${group.slug}`}>{group.title}</Link>
        </>
      )}
    </nav>
  );
}

function LibraryPage({ slug, crumbs }: { slug: string; crumbs: ReactNode }) {
  const page = useQuery({
    queryKey: ["library", slug],
    queryFn: () => unwrap(api.GET("/v1/admin/library/pages/{slug}", { params: { path: { slug } } })),
    staleTime: 5 * 60_000,
  });
  const t = useT();
  const updated = useUpdated();
  useEffect(() => {
    if (page.data) document.title = t("library.page.documentTitle", { title: page.data.item.title });
  }, [page.data, t]);
  if (page.isLoading) return <Spinner />;
  if (page.error || !page.data) return <Alert tone="danger">{errorMessage(page.error)}</Alert>;
  const { item, html, data } = page.data;
  const view = item.view;
  const header = view === "article" || view === "eclipse" ? null : (
    <PageHead
      title={item.title}
      description={
        <>
          {item.summary} <span className="lib-muted">{t("library.updatedSentence", { date: updated(item.updated_on) })}</span>
        </>
      }
    />
  );
  return (
    <div className={view === "eclipse" ? "lib-doc lib-eclipse" : "lib-doc"}>
      {crumbs}
      {header}
      {html && <LibraryContent html={html} mounts={view === "eclipse" && data ? eclipseMounts(data as unknown as EclipseData) : undefined} />}
      {view === "stack" && data && <StackView data={data as unknown as StackData} />}
      {view === "sources" && data && <SourcesView data={data as unknown as SourcesData} />}
      {view === "tests" && data && <TestCasesView data={data as unknown as TestsData} />}
    </div>
  );
}
